import { pick } from 'dot-object'
import {
  BACKEND_DOCUMENT_CHANNEL,
  DELIVERY_METHOD
} from 'constants/documentDelivery'
import type {
  BackendDocumentChannel,
  DeliveryMethod
} from 'constants/documentDelivery'
import type {
  BackendDocumentDeliveryPreferences,
  DocumentKey
} from 'interfaces/documentDelivery.interface'
import type { FieldChange } from 'stores/initialization/traversalInitialization/TraversalStore.types'
import type { Data } from 'interfaces/ApolloResponse.interface'
import type RootStore from 'stores/RootStore'
import isSecondDebtor from 'utils/formDependencies/conditions/hasSecondDebtor'

const uniformPreferences = (
  channel: BackendDocumentChannel
): BackendDocumentDeliveryPreferences => ({
  preContractualInformation: channel,
  withdrawalNotificationMedium: channel,
  withdrawalReminder: channel,
  postContractualInformation: channel
})

/** Every document goes the same way, so a second debtor simply follows the first. */
export const buildDocumentDeliveryFieldChanges = (
  method: Exclude<DeliveryMethod, typeof DELIVERY_METHOD.PER_DOCUMENT>,
  hasSecondDebtor: boolean
): FieldChange<unknown>[] => {
  const preferences = uniformPreferences(
    method === DELIVERY_METHOD.EMAIL
      ? BACKEND_DOCUMENT_CHANNEL.DIGITAL
      : BACKEND_DOCUMENT_CHANNEL.POSTAL
  )

  return [
    { path: 'debtors.primary.documentDeliveryPreferences', value: preferences },
    ...(hasSecondDebtor
      ? [
          {
            path: 'debtors.secondary.documentDeliveryPreferences',
            value: preferences
          },
          {
            path: 'debtors.secondary.documentDeliveryPreferencesFollowPrimary',
            value: true
          }
        ]
      : [])
  ]
}

/** The second debtor takes over the first debtor's choices until it is switched off. */
export const followsFirstDebtor = (data: Data) =>
  data.debtors.secondary.documentDeliveryPreferencesFollowPrimary !== false

/** The method the saved preferences describe, e.g. after a resume; `null` when nothing was saved yet. */
export const getDocumentDeliveryMethod = (
  data: Data
): DeliveryMethod | null => {
  const primary = data.debtors.primary.documentDeliveryPreferences
  const followsPrimary = !isSecondDebtor(data) || followsFirstDebtor(data)

  if (followsPrimary && !getValues(primary).some(Boolean)) {
    console.log('primary null', primary)
    return null
  }
  console.log('primary out', primary)
  return (
    (followsPrimary && getUniformMethod(primary)) ||
    DELIVERY_METHOD.PER_DOCUMENT
  )
}

/** Defaults every document to digital, unless the traversal already holds preferences (e.g. after a resume). */
export const applyDefaultDocumentDelivery = ({ traversal }: RootStore) => {
  if (getDocumentDeliveryMethod(traversal.data)) {
    console.log('Document delivery method already set')
    return
  }
  console.log('Document delivery method already setting now')
  traversal.handleMultiplePathFormFieldChanges(
    buildDocumentDeliveryFieldChanges(
      DELIVERY_METHOD.EMAIL,
      isSecondDebtor(traversal.data)
    )
  )
}

/** Removes the saved preferences, e.g. when the checkbox is unticked. */
export const buildDocumentDeliveryResetChanges = (
  hasSecondDebtor: boolean
): FieldChange<unknown>[] => [
  { path: 'debtors.primary.documentDeliveryPreferences', value: undefined },
  ...(hasSecondDebtor
    ? [
        {
          path: 'debtors.secondary.documentDeliveryPreferences',
          value: undefined
        },
        {
          path: 'debtors.secondary.documentDeliveryPreferencesFollowPrimary',
          value: undefined
        }
      ]
    : [])
]

const BACKEND_PREFERENCE_KEY: Record<
  DocumentKey,
  keyof BackendDocumentDeliveryPreferences
> = {
  preContractual: 'preContractualInformation',
  withdrawalMedium: 'withdrawalNotificationMedium',
  withdrawalReminder: 'withdrawalReminder',
  postContractual: 'postContractualInformation'
}

const DOCUMENT_KEYS = Object.keys(BACKEND_PREFERENCE_KEY) as DocumentKey[]

const getValues = (
  preferences: Partial<BackendDocumentDeliveryPreferences> = {}
) => DOCUMENT_KEYS.map((key) => preferences[BACKEND_PREFERENCE_KEY[key]])

/** All four documents have a choice. */
export const isCompleteDocumentPreferences = (
  preferences?: Partial<BackendDocumentDeliveryPreferences>
) => getValues(preferences).every(Boolean)

/** The single method that describes all documents, `null` when they are mixed or incomplete. */
const getUniformMethod = (
  preferences?: Partial<BackendDocumentDeliveryPreferences>
) => {
  const values = getValues(preferences)

  if (values.every((value) => value === BACKEND_DOCUMENT_CHANNEL.DIGITAL)) {
    return DELIVERY_METHOD.EMAIL
  }

  if (values.every((value) => value === BACKEND_DOCUMENT_CHANNEL.POSTAL)) {
    return DELIVERY_METHOD.POST
  }

  return null
}

export const getPreferencePath = (
  debtor: 'primary' | 'secondary',
  key: DocumentKey
) =>
  `debtors.${debtor}.documentDeliveryPreferences.${BACKEND_PREFERENCE_KEY[key]}`

/** The flag goes out right away; the first debtor's chosen documents are copied when it is switched on. */
export const buildDocumentDeliveryFollowPrimaryChanges = (
  data: Data,
  isSecondSameAsFirst: boolean
): FieldChange<unknown>[] => {
  if (!isSecondDebtor(data)) {
    return []
  }

  return [
    {
      path: 'debtors.secondary.documentDeliveryPreferencesFollowPrimary',
      value: isSecondSameAsFirst
    },
    ...(isSecondSameAsFirst
      ? DOCUMENT_KEYS.flatMap((key) => {
          const value = pick(getPreferencePath('primary', key), data)

          return value
            ? [{ path: getPreferencePath('secondary', key), value }]
            : []
        })
      : [])
  ]
}
